import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form/form-field'
import {
  FORM_ID,
  type FormValues,
  schema,
} from '@/dialogs/CreateSalesTemplateDialog/validation.ts'
import type { SalesEditorTemplate } from '@/modules/sales-editor/state/types'
import { handleError } from '@/utils/error'

import type { DialogProps } from '../types'

interface Props extends DialogProps {
  isCopy?: boolean
  data?: SalesEditorTemplate['data']
  onSave: (name: string) => void
}

export const CreateSalesTemplateDialog = ({
  isOpen,
  onClose,
  onSave,
}: Props) => {
  const form = useForm<FormValues>({
    defaultValues: {
      name: '',
    },
    resolver: zodResolver(schema),
  })

  const onSubmit = ({ name }: FormValues) => {
    try {
      onSave(name)
      onClose()
    } catch (error: Error | unknown) {
      handleError(error, 'An error occurred while creating the template')
    }
  }

  return (
    <Dialog
      bigDialog
      childrenClassName="space-y-6"
      panelClassName="lg:max-w-[560px]"
      show={isOpen}
      onClose={onClose}
      afterLeave={form.reset}
    >
      <Dialog.Header>
        <Dialog.Title>Create new template</Dialog.Title>
      </Dialog.Header>

      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          id={FORM_ID}
        >
          <FormField
            autoFocus
            name="name"
            label="Template name"
          />
        </form>
      </FormProvider>

      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          type="submit"
          form={FORM_ID}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
